import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  Typography,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import {
  MeasurementCardDetails,
  StackNumberingEnumDto,
  ExportFormatEnumDto,
} from '.generated/api';
import { useTranslation } from 'react-i18next';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';
import { ApiService } from 'services/apiService';

const MeasurementCard: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({
    stackNumbering: StackNumberingEnumDto.AUTO_GENERATED,
    measurementCardId: '',
    measurementCardName: '',
    exportFormat: ExportFormatEnumDto.CSV,
  });
  const [isMounted, setIsMounted] = useState(true);

  const [measurementCards, setMeasurementCards] = useState<
    MeasurementCardDetails[]
  >([]);

  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [organization]);

  const resetInput = () => {
    setInput((inputs) => ({
      ...inputs,
      stackNumbering:
        organization?.stackNumbering || StackNumberingEnumDto.AUTO_GENERATED,
    }));

    setInput((inputs) => ({
      ...inputs,
      measurementCardId: organization?.measurementCardId || '',
    }));

    setInput((inputs) => ({
      ...inputs,
      measurementCardName: organization?.measurementCardName || '',
    }));

    setInput((inputs) => ({
      ...inputs,
      exportFormat:
        organization?.defaultExportFormat || ExportFormatEnumDto.CSV,
    }));
  };

  const handleSelect = (
    inputName: string,
    e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const { value } = e.target;
    setInput((inputs) => ({ ...inputs, [inputName]: value }));
  };

  const handleSave = () => {
    if (organization) {
      setIsLoading(true);

      dispatch(
        organizationActions.editMeasurementCard(
          organization?.organizationId,
          input.measurementCardId,
          input.stackNumbering,
          input.exportFormat,
          (error) => {
            if (isMounted) {
              setIsLoading(false);
              setEditMode(!!error);
            }
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  useEffect(() => {
    if (editMode) {
      ApiService.getMeasurementCardList()
        .then((response) => {
          const details = response?.data?.details;
          if (details && details.length > 0) {
            setMeasurementCards(details);
            setInput((inputs) => ({
              ...inputs,
              measurementCard: details[0].measurementCardId || '',
            }));
          }
        })
        .catch((error) => {
          dispatch(notificationActions.enqueueErrorResponse(error));
        });
    }
  }, [editMode]);

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      title={t('organization_page.measurement_card.title')}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      requireGlobalAdmin
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {t('organization_page.measurement_card.labels.stack_numbering')}
              </InputLabel>
              <Select
                value={input.stackNumbering}
                onChange={(e) => handleSelect('stackNumbering', e)}
                label={t(
                  'organization_page.measurement_card.labels.stack_numbering',
                )}
                id="stackDD"
              >
                <MenuItem value={StackNumberingEnumDto.AUTO_GENERATED}>
                  {t(
                    'organization_page.measurement_card.stack_numbering_types.autogenerated',
                  )}
                </MenuItem>
                <MenuItem value={StackNumberingEnumDto.MANUEL}>
                  {t(
                    'organization_page.measurement_card.stack_numbering_types.manuel',
                  )}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {t(
                  'organization_page.measurement_card.labels.measurement_card',
                )}
              </InputLabel>
              <Select
                value={input.measurementCardId}
                onChange={(e) => handleSelect('measurementCardId', e)}
                label={t(
                  'organization_page.measurement_card.labels.measurement_card',
                )}
                id="cardDD"
              >
                {measurementCards?.map((card, index) => {
                  return (
                    <MenuItem key={index} value={card.measurementCardId || ''}>
                      {card.measurementCardName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          {/*
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {t('organization_page.measurement_card.labels.export_format')}
              </InputLabel>
              <Select
                value={input.exportFormat}
                onChange={(e) => handleSelect('exportFormat', e)}
                label={t(
                  'organization_page.measurement_card.labels.export_format',
                )}
                id="stackDD"
              >
                <MenuItem value={ExportFormatEnumDto.CSV}>
                  {t('organization_page.measurement_card.export_formats.csv')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          */}
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            {t('organization_page.measurement_card.labels.measurement_card')}
            :&nbsp;
            {input.measurementCardName}
          </Typography>
          <Typography variant="body1">
            {t('organization_page.measurement_card.labels.stack_numbering')}
            :&nbsp;
            {input.stackNumbering}
          </Typography>
          {/*
          <Typography variant="body1">
            {t('organization_page.measurement_card.labels.export_format')}
            :&nbsp;
            {input.exportFormat}
          </Typography>
          */}
        </>
      )}
    </EditCard>
  );
};

export default MeasurementCard;
